import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_META = {
  not_started:  { label: 'Not Started',  bs: 'secondary', icon: 'bi-hourglass' },
  yet_to_start: { label: 'Yet To Start', bs: 'secondary', icon: 'bi-hourglass-split' },
  in_progress:  { label: 'In Progress',  bs: 'info',      icon: 'bi-play-circle' },
  awaiting:     { label: 'Awaiting',     bs: 'warning',   icon: 'bi-pause-circle' },
  in_review:    { label: 'In Review',    bs: 'primary',   icon: 'bi-search' },
  completed:    { label: 'Completed',    bs: 'success',   icon: 'bi-check-circle-fill' },
  on_hold:      { label: 'On Hold',      bs: 'dark',      icon: 'bi-pause-btn' },
  transferred:  { label: 'Transferred',  bs: 'secondary', icon: 'bi-arrow-left-right' },
  delayed:      { label: 'Overdue',      bs: 'warning',   icon: 'bi-exclamation-triangle' },
  incomplete:   { label: 'Incomplete',   bs: 'danger',    icon: 'bi-x-circle' },
  pending:      { label: 'Not Started',  bs: 'secondary', icon: 'bi-hourglass' },
  cancelled:    { label: 'Incomplete',   bs: 'danger',    icon: 'bi-x-circle' },
};

const PRIORITY_META = {
  low:    { bs: 'success', label: 'Low' },
  medium: { bs: 'primary', label: 'Medium' },
  high:   { bs: 'warning', label: 'High' },
  urgent: { bs: 'danger',  label: 'Urgent' },
};

const CATEGORY_LABEL = {
  teaching: 'Teaching', admin: 'Admin', placement: 'Placement',
  assessment: 'Assessment', other: 'Other',
};

const TASK_TYPE_LABEL = {
  assigned: 'Assigned Task',
  contribution: 'Contribution Task',
};

const CONV_TYPE_BS = {
  comment: 'primary', note: 'secondary', status_update: 'warning',
  question: 'info', reply: 'dark',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtDT   = (d) => d ? new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true }) : '—';
const isOverdue = (task) => {
  const d = task.targetDate || task.dueDate;
  if (task.timelinessStatus === 'overdue') return true;
  if (!d || ['completed','incomplete','cancelled'].includes(task.status)) return false;
  const deadline = new Date(d);
  deadline.setHours(23, 59, 59, 999);
  return deadline < new Date();
};

// When category === 'other', the user-typed specifics are stamped into
// description as [Category: Foo Bar]. Pull that back out for display + edit.
const OTHER_TAG_RE = /^\[Category:\s*([^\]]+)\]\s*\n?/;
const extractOtherCategory = (task) => {
  if (!task || task.category !== 'other') return '';
  const m = (task.description || '').match(OTHER_TAG_RE);
  return m ? m[1].trim() : '';
};
// Strip the tag from the description for display.
const cleanDescription = (task) => {
  if (!task) return '';
  if (task.category !== 'other') return task.description || '';
  return (task.description || '').replace(OTHER_TAG_RE, '');
};
// Display-friendly category label including the user-typed "other" value.
const displayCategory = (task, base) => {
  const other = extractOtherCategory(task);
  return task?.category === 'other' && other ? `Other — ${other}` : (base || task?.category || 'other');
};

// ─── Task Form Modal ─────────────────────────────────────────────────────────

function TaskFormModal({ show, mode, initialData, groupedUsers, onClose, onSaved }) {
  const empty = { title: '', description: '', assignedTo: '', priority: 'medium',
                  category: 'other', categoryOther: '', targetDate: '', startDate: '', taskType: 'assigned', contributionLevel: 'medium', endTime: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (show) {
      if (mode === 'edit' && initialData) {
        setForm({
          title:       initialData.title || '',
          // Strip the [Category: ...] tag for editing — it's re-added on save.
          description: cleanDescription(initialData),
          assignedTo:  initialData.assignedTo?._id || initialData.assignedTo || '',
          priority:    initialData.priority || 'medium',
          category:    initialData.category || 'other',
          taskType:    initialData.taskType || 'assigned',
          contributionLevel: initialData.contributionLevel || 'medium',
          // Restore the user-typed "other" value from the description tag we
          // stamped on save, so editing keeps the original text.
          categoryOther: extractOtherCategory(initialData) || '',
          targetDate:  initialData.targetDate ? new Date(initialData.targetDate).toISOString().split('T')[0] : '',
          startDate:   initialData.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : '',
          endTime:     initialData.endTime ? new Date(initialData.endTime).toISOString().slice(0, 16) : '',
        });
      } else {
        setForm(empty);
      }
      setErr('');
    }
    // eslint-disable-next-line
  }, [show, mode, initialData]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    if (form.taskType === 'assigned' && !form.assignedTo)   { setErr('Please select an assignee.'); return; }
    if (form.startDate && form.targetDate && new Date(form.targetDate) < new Date(form.startDate)) {
      setErr('Target date cannot be earlier than start date.');
      return;
    }
    if (form.category === 'other' && !form.categoryOther.trim()) {
      setErr('Please specify what kind of task it is (in the Category > Other box).');
      return;
    }
    setSaving(true);
    // Build payload — when category is "other", stamp the user-typed label
    // into the description so it survives backend validation (the schema
    // doesn't currently have a `categoryOther` field, but description does).
    const { categoryOther, description, ...rest } = form;
    const finalDesc = (form.category === 'other' && categoryOther.trim())
      ? `[Category: ${categoryOther.trim()}]\n${description || ''}`.trim()
      : description;
    const payload = { ...rest, assignedTo: form.taskType === 'assigned' ? form.assignedTo : '', description: finalDesc };
    try {
      if (mode === 'edit') {
        await api.put(`/shared/tasks/${initialData._id}`, payload);
      } else {
        await api.post('/shared/tasks', payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed.');
    }
    setSaving(false);
  };

  if (!show) return null;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{mode === 'edit' ? 'Edit Task' : 'Assign New Task'}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{err}</div>}
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Task Title <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} required />
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Task Type</label>
                  <div className="d-flex gap-2">
                    {Object.entries(TASK_TYPE_LABEL).map(([key, label]) => (
                      <button key={key} type="button" className={`btn btn-sm ${form.taskType === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => set('taskType', key)}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">Assign To <span className="text-danger">*</span></label>
                  {form.taskType === 'assigned' ? (
                    <select className="form-select" value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)} required>
                      <option value="">Select assignee...</option>
                      {Object.entries(groupedUsers || {}).map(([grp, users]) => (
                        <optgroup key={grp} label={grp}>
                          {users.map(u => (
                            <option key={u._id} value={u._id}>{u.name} ({u.role?.replace(/_/g,' ')})</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  ) : (
                    <div className="form-control bg-light">Faculty can pick up this task later from the task list.</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
                    {Object.entries(PRIORITY_META).map(([v, m]) => (
                      <option key={v} value={v}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Contribution Level</label>
                  <select className="form-select" value={form.contributionLevel} onChange={e => set('contributionLevel', e.target.value)}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
                    {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  {/* When "Other" is picked, ask the user to specify what it is.
                      The text is stored on the form so it can be sent alongside
                      the category — backend stores it as a tag/note. */}
                  {form.category === 'other' && (
                    <input
                      className="form-control mt-2"
                      placeholder="Please specify what kind of task… (e.g. Event coordination, Maintenance, Audit)"
                      value={form.categoryOther || ''}
                      onChange={e => set('categoryOther', e.target.value)}
                      autoFocus
                    />
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Start Date</label>
                  <input type="date" className="form-control" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Target Date</label>
                  <input type="date" className="form-control" min={form.startDate || undefined} value={form.targetDate} onChange={e => set('targetDate', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">End Time</label>
                  <input type="datetime-local" className="form-control" value={form.endTime} onChange={e => set('endTime', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                {mode === 'edit' ? 'Save Changes' : 'Create Task'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Task Detail Modal (with conversation) ───────────────────────────────────

function TaskDetailModal({ show, task, currentUser, onClose, onStatusChange, onConversationAdded }) {
  const [convType, setConvType] = useState('comment');
  const [message, setMessage]   = useState('');
  const [sending, setSending]   = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [extensionDate, setExtensionDate] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  const [requestingExtension, setRequestingExtension] = useState(false);

  useEffect(() => {
    if (show && task) setNewStatus(task.status);
  }, [show, task]);

  if (!show || !task) return null;

  const roleKey = currentUser?.roleKey || currentUser?.role?.key || '';
  // The login response sets `user.id` (not `_id`) — see backend
  // /auth/login. Reading `_id` gave undefined, which made every assignee /
  // creator check silently fail and locked faculty out of changing status
  // after their first save. Fall back to `_id` for any other code path
  // that does store the Mongo field directly.
  const userId = (currentUser?.id || currentUser?._id || '').toString();
  const isAssignee = (task.assignedTo?._id || task.assignedTo)?.toString() === userId;
  const isClaimedContributor = (task.contributeClaimedBy?._id || task.contributeClaimedBy)?.toString() === userId;
  const isCreator  = (task.assignedBy?._id || task.assignedBy)?.toString() === userId;
  const adminRole  = ['admin'].includes(roleKey) || currentUser?.userType === 'super_admin';
  const canChangeStatus = isAssignee || isClaimedContributor || isCreator || adminRole;
  const canClaimContribution = task.taskType === 'contribution' && !task.contributeClaimedBy && !isCreator && !adminRole;

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post(`/shared/tasks/${task._id}/conversation`, { type: convType, message });
      setMessage('');
      onConversationAdded();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || newStatus === task.status) return;
    await onStatusChange(task._id, newStatus);
  };

  const handleRequestExtension = async () => {
    if (!extensionDate || !extensionReason.trim()) return;
    setRequestingExtension(true);
    try {
      await api.post(`/shared/tasks/${task._id}/request-extension`, {
        requestedDate: extensionDate,
        reason: extensionReason,
      });
      setExtensionDate('');
      setExtensionReason('');
      onConversationAdded();
    } catch (e) {
      console.error(e);
    }
    setRequestingExtension(false);
  };

  const sortedConv = [...(task.conversation || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const sm = STATUS_META[task.status] || STATUS_META.not_started;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: 900 }}>
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-1">{task.title}</h5>
              <div className="d-flex gap-1 flex-wrap">
                <span className={`badge bg-${sm.bs}`}><i className={`bi ${sm.icon} me-1`}></i>{sm.label}</span>
                <span className={`badge bg-${PRIORITY_META[task.priority]?.bs || 'primary'}`}>{task.priority}</span>
                <span className="badge bg-light text-dark border">{displayCategory(task, CATEGORY_LABEL[task.category])}</span>
                {isOverdue(task) && <span className="badge bg-danger">OVERDUE</span>}
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body p-0">
            <div className="row g-0" style={{ minHeight: 460 }}>
              {/* Left: Details */}
              <div className="col-md-5 border-end p-3">
                <h6 className="text-muted mb-1" style={{ fontSize: 12 }}>Description</h6>
                <p style={{ fontSize: 13 }}>{cleanDescription(task) || 'No description provided.'}</p>

                <hr />

                {[
                  ['Task Type', TASK_TYPE_LABEL[task.taskType] || 'Assigned Task'],
                  ['Assigned To', `${task.assignedTo?.firstName || ''} ${task.assignedTo?.lastName || ''}`],
                  ['Role',        task.assignedTo?.roleKey?.replace(/_/g, ' ') || '—'],
                  ['Created By',  `${task.assignedBy?.firstName || ''} ${task.assignedBy?.lastName || ''}`],
                  ['Start Date',  fmtDate(task.startDate)],
                  ['Target Date', fmtDate(task.targetDate || task.dueDate)],
                  ['End Time',    fmtDT(task.endTime)],
                  ['Started At',  fmtDate(task.startedAt)],
                  ['Completed',   fmtDate(task.completedAt)],
                  ['Timeliness',  (task.timelinessStatus || 'pending').replace(/_/g, ' ')],
                  ['Contribution Level', task.contributionLevel || '—'],
                  ['Category',    displayCategory(task, CATEGORY_LABEL[task.category])],
                ].map(([label, value]) => (
                  <div key={label} className="d-flex justify-content-between mb-1">
                    <span style={{ fontSize: 12, color: '#6c757d' }}>{label}</span>
                    <span style={{ fontSize: 12, fontWeight: 500, textTransform: 'capitalize' }}>{value}</span>
                  </div>
                ))}

                {canChangeStatus && (
                  <>
                    <hr />
                    <h6 style={{ fontSize: 13 }}>Update Status</h6>
                    <div className="d-flex gap-2 align-items-center">
                      <select className="form-select form-select-sm" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                        {Object.entries(STATUS_META)
                          .filter(([k]) => !['pending','cancelled'].includes(k))
                          .map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                      </select>
                      <button className="btn btn-primary btn-sm" onClick={handleStatusUpdate}
                        disabled={newStatus === task.status}>Save</button>
                    </div>
                  </>
                )}

                {canClaimContribution && (
                  <>
                    <hr />
                    <button
                      className="btn btn-success btn-sm w-100"
                      onClick={async () => {
                        try {
                          await api.post(`/shared/tasks/${task._id}/claim`);
                          onConversationAdded();
                        } catch (e) { console.error(e); }
                      }}
                    >
                      Claim Contribution Task
                    </button>
                  </>
                )}

                {canChangeStatus && (
                  <>
                    <hr />
                    <h6 style={{ fontSize: 13 }}>Request Extension</h6>
                    <input
                      type="date"
                      className="form-control form-control-sm mb-2"
                      value={extensionDate}
                      onChange={e => setExtensionDate(e.target.value)}
                    />
                    <textarea
                      className="form-control form-control-sm mb-2"
                      rows={2}
                      placeholder="Reason for extending the target date"
                      value={extensionReason}
                      onChange={e => setExtensionReason(e.target.value)}
                    />
                    <button
                      className="btn btn-outline-primary btn-sm w-100"
                      onClick={handleRequestExtension}
                      disabled={requestingExtension || !extensionDate || !extensionReason.trim()}
                    >
                      {requestingExtension ? 'Saving...' : 'Submit Extension'}
                    </button>
                  </>
                )}

                {/* Activity Log */}
                {task.activityLog?.length > 0 && (
                  <>
                    <hr />
                    <h6 style={{ fontSize: 13 }}>Activity Log</h6>
                    <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                      {[...(task.activityLog || [])].reverse().map((a, i) => (
                        <div key={i} className="mb-2">
                          <div style={{ fontSize: 11, color: '#6c757d' }}>{fmtDT(a.createdAt)} · {a.performedByName}</div>
                          <div style={{ fontSize: 12 }}>{a.action}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Right: Conversation */}
              <div className="col-md-7 d-flex flex-column">
                <div className="p-2 border-bottom bg-light">
                  <strong style={{ fontSize: 14 }}>Conversation ({task.conversation?.length || 0})</strong>
                </div>

                {/* Messages */}
                <div className="flex-grow-1 p-3" style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {sortedConv.length === 0 ? (
                    <p className="text-muted text-center mt-4" style={{ fontSize: 13 }}>
                      No messages yet. Start the conversation below.
                    </p>
                  ) : sortedConv.map((c, i) => (
                    <div key={i} className="d-flex gap-2 mb-3">
                      <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 30, height: 30, fontSize: 12 }}>
                        {c.userName?.charAt(0) || '?'}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex gap-1 align-items-center mb-1">
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{c.userName}</span>
                          <span className={`badge bg-${CONV_TYPE_BS[c.type] || 'secondary'}`}
                            style={{ fontSize: 9 }}>{c.type}</span>
                          <span style={{ fontSize: 11, color: '#6c757d' }}>{fmtDT(c.createdAt)}</span>
                        </div>
                        <div className="border rounded p-2 bg-white" style={{ fontSize: 13 }}>
                          {c.message}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Compose */}
                <div className="p-2 border-top">
                  <div className="d-flex gap-1 mb-2">
                    {['comment','note','question','reply'].map(t => (
                      <button key={t} className={`btn btn-sm ${convType === t ? `btn-${CONV_TYPE_BS[t]}` : 'btn-outline-secondary'}`}
                        style={{ fontSize: 11, padding: '2px 8px' }}
                        onClick={() => setConvType(t)}>
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="d-flex gap-2">
                    <textarea className="form-control form-control-sm" rows={2}
                      placeholder={`Write a ${convType}...`}
                      value={message} onChange={e => setMessage(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }} />
                    <button className="btn btn-primary btn-sm align-self-end"
                      onClick={handleSendMessage} disabled={!message.trim() || sending}>
                      {sending ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-send"></i>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Report Tab ──────────────────────────────────────────────────────────────

function ReportTab() {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/shared/tasks/stats/report');
      setData(r.data?.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <button className="btn btn-outline-secondary btn-sm" onClick={load}>
          <i className="bi bi-arrow-clockwise me-1"></i>Refresh
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead>
            <tr>
              {['Employee','Role','Total','Not Started','In Progress','Completed','Delayed','Incomplete','Completion %'].map(h => (
                <th key={h} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 500, fontSize: 13 }}>{row.employeeName}</td>
                <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{row.role?.replace(/_/g,' ')}</td>
                <td style={{ fontSize: 13 }}>{row.total}</td>
                <td style={{ fontSize: 13 }}>{row.not_started}</td>
                <td style={{ fontSize: 13 }}>{row.in_progress}</td>
                <td style={{ fontSize: 13, color: '#198754', fontWeight: 600 }}>{row.completed}</td>
                <td style={{ fontSize: 13, color: '#fd7e14' }}>{row.delayed}</td>
                <td style={{ fontSize: 13, color: '#dc3545' }}>{row.incomplete}</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <div className="progress flex-grow-1" style={{ height: 8 }}>
                      <div className={`progress-bar bg-${Math.round(row.completionRate) >= 75 ? 'success' : Math.round(row.completionRate) >= 40 ? 'warning' : 'danger'}`}
                        style={{ width: `${Math.round(row.completionRate)}%` }}></div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, minWidth: 32 }}>{Math.round(row.completionRate)}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr><td colSpan={9} className="text-center text-muted py-4">No report data available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExtensionRequestsTab({ canReview, onUpdated }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/shared/tasks/extension-requests', { params: { status } });
      setRows(r.data?.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [status]);

  useEffect(() => { if (canReview) load(); }, [canReview, load]);

  const review = async (taskId, requestId, action) => {
    try {
      await api.post(`/shared/tasks/${taskId}/extension-requests/${requestId}/review`, { action });
      load();
      onUpdated?.();
    } catch (e) { console.error(e); }
  };

  if (!canReview) return null;
  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <select className="form-select form-select-sm" style={{ width: 180 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
        <button className="btn btn-outline-secondary btn-sm" onClick={load}><i className="bi bi-arrow-clockwise me-1"></i>Refresh</button>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead>
            <tr>
              <th style={{ fontSize: 12 }}>Task</th>
              <th style={{ fontSize: 12 }}>Requested By</th>
              <th style={{ fontSize: 12 }}>Current Date</th>
              <th style={{ fontSize: 12 }}>Requested Date</th>
              <th style={{ fontSize: 12 }}>Reason</th>
              <th style={{ fontSize: 12 }}>Status</th>
              <th style={{ fontSize: 12 }} className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.extensionRequest?._id}>
                <td style={{ fontSize: 13, fontWeight: 500 }}>{row.title}</td>
                <td style={{ fontSize: 12 }}>{row.requestedByUser ? `${row.requestedByUser.firstName} ${row.requestedByUser.lastName}` : '—'}</td>
                <td style={{ fontSize: 12 }}>{fmtDate(row.targetDate)}</td>
                <td style={{ fontSize: 12 }}>{fmtDate(row.extensionRequest?.requestedDate)}</td>
                <td style={{ fontSize: 12, maxWidth: 260 }} className="text-truncate" title={row.extensionRequest?.reason}>{row.extensionRequest?.reason}</td>
                <td><span className={`badge bg-${row.extensionRequest?.status === 'approved' ? 'success' : row.extensionRequest?.status === 'rejected' ? 'danger' : 'warning'}`}>{row.extensionRequest?.status || 'pending'}</span></td>
                <td className="text-end">
                  {row.extensionRequest?.status === 'pending' && (
                    <div className="d-flex gap-1 justify-content-end">
                      <button className="btn btn-sm btn-outline-success" onClick={() => review(row._id, row.extensionRequest._id, 'approved')}>Approve</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => review(row._id, row.extensionRequest._id, 'rejected')}>Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">No extension requests found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function TasksPage() {
  const { user, role: authRole } = useAuth();
  const location = useLocation();

  const roleKey = authRole?.key || user?.roleKey || '';
  const isAdminRole   = ['admin'].includes(roleKey) || user?.userType === 'super_admin';
  const isManagerRole = roleKey === 'manager';
  const isTLRole      = roleKey === 'team_lead';
  const isFacultyRole = roleKey === 'teacher' || roleKey === 'faculty';
  const isStudentRole = roleKey === 'student';
  const canAssign     = isAdminRole || isManagerRole || isTLRole;
  const canReport     = isAdminRole || isManagerRole;

  // ── State ──
  const [tab, setTab]         = useState(0);
  const [tasks, setTasks]     = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupedUsers, setGroupedUsers] = useState({});
  const [stats, setStats]     = useState({});

  const [search, setSearch]           = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');

  const [formModal, setFormModal]   = useState({ show: false, mode: 'create', data: null });
  const [detailTask, setDetailTask] = useState(null);
  const [deleteTask, setDeleteTask] = useState(null);
  const [toast, setToast]           = useState({ show: false, message: '', type: 'success' });
  const [openedTaskFromQuery, setOpenedTaskFromQuery] = useState('');

  // ── Fetch ──
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus)   params.status   = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      if (filterEmployee) params.assignedTo = filterEmployee;

      const calls = [
        api.get('/shared/tasks', { params }).catch(() => ({ data: { records: [] } })),
        api.get('/shared/tasks/my').catch(() => ({ data: { records: [] } })),
        api.get('/shared/tasks/stats/summary').catch(() => ({ data: {} })),
      ];
      if (canAssign) {
        calls.push(api.get('/shared/tasks/assignable-users').catch(() => ({ data: {} })));
      }

      const results = await Promise.all(calls);
      // GET /shared/tasks → paginated() → r.data.records
      // GET /shared/tasks/my → success(arr) → r.data.records
      // GET /shared/tasks/stats/summary → success(obj-with-total) → spread at top level
      setTasks(results[0].data?.records || results[0].data?.data || []);
      setMyTasks(results[1].data?.records || results[1].data?.data || []);
      setStats(results[2].data || {});
      if (canAssign && results[3]) {
        setGroupedUsers(results[3].data?.grouped || results[3].data?.data?.grouped || {});
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterStatus, filterPriority, filterEmployee, canAssign]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Handlers ──
  const handleStatusChange = async (id, status) => {
    try {
      const res = await api.put(`/shared/tasks/${id}/status`, { status });
      const updated = res.data?.data;
      if (updated) {
        setTasks(ts => ts.map(t => t._id === id ? updated : t));
        // Faculty / student tabs read from `myTasks` (loaded from /tasks/my),
        // not `tasks`. Without this update the row stayed on the old status
        // until a hard refresh, which made it look like the change "didn't
        // save" or that the row was no longer editable.
        setMyTasks(mts => mts.map(t => t._id === id ? updated : t));
        if (detailTask?._id === id) setDetailTask(updated);
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async () => {
    if (!deleteTask) return;
    try {
      await api.delete(`/shared/tasks/${deleteTask._id}`);
      setTasks(ts => ts.filter(t => t._id !== deleteTask._id));
      setDeleteTask(null);
      setToast({ show: true, message: 'Task deleted.', type: 'success' });
    } catch (e) { console.error(e); }
  };

  const handleConversationAdded = async () => {
    if (!detailTask) return;
    try {
      const res = await api.get(`/shared/tasks/${detailTask._id}`);
      const updated = res.data?.data;
      if (updated) {
        setDetailTask(updated);
        setTasks(ts => ts.map(t => t._id === updated._id ? updated : t));
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const taskId = params.get('taskId');
    if (!taskId || openedTaskFromQuery === taskId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/shared/tasks/${taskId}`);
        if (cancelled) return;
        const updated = res.data?.data;
        if (!updated) return;
        setDetailTask(updated);
        setOpenedTaskFromQuery(taskId);
        api.post(`/shared/tasks/${taskId}/acknowledge`).catch(() => {});
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [location.search, openedTaskFromQuery]);

  const handleFormSaved = () => {
    setFormModal({ show: false, mode: 'create', data: null });
    setToast({ show: true, message: 'Task saved successfully.', type: 'success' });
    fetchAll();
  };

  // ── Filtering ──
  const applySearch = (list) => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(t =>
      t.title?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      `${t.assignedTo?.firstName} ${t.assignedTo?.lastName}`.toLowerCase().includes(q)
    );
  };

  const filtered     = applySearch(tasks);
  const myFiltered   = applySearch(myTasks);
  const meId = (user?.id || user?._id || '').toString();
  const createdTasks = filtered.filter(t => (t.assignedBy?._id || t.assignedBy)?.toString() === meId);
  const overdueTasks = filtered.filter(t => isOverdue(t));

  const tabData = (isFacultyRole || isStudentRole)
    ? myFiltered
    : [filtered, myFiltered, createdTasks, overdueTasks][tab] || filtered;

  // Stat helper
  const sc = (s) => tasks.filter(t => {
    const n = s === 'delayed'
      ? (isOverdue(t) ? 'delayed' : t.status)
      : t.status === 'pending'
        ? 'not_started'
        : t.status === 'cancelled'
          ? 'incomplete'
          : t.status;
    return n === s;
  }).length;

  const tabItems = [];
  if (!isFacultyRole && !isStudentRole) tabItems.push({ label: `All (${filtered.length})`, idx: 0 });
  tabItems.push({ label: `My Tasks (${myFiltered.length})`, idx: isFacultyRole || isStudentRole ? 0 : 1 });
  if (canAssign) tabItems.push({ label: `Created (${createdTasks.length})`, idx: 2 });
  if (!isFacultyRole && !isStudentRole) tabItems.push({ label: `Overdue (${overdueTasks.length})`, idx: 3 });
  if (canAssign) tabItems.push({ label: 'Extensions', idx: 98, icon: 'bi-calendar-plus' });
  if (canReport) tabItems.push({ label: 'Report', idx: 99, icon: 'bi-bar-chart' });

  const isExtensionTab = tab === 98;
  const isReportTab = tab === 99;
  const employeeOptions = Object.values(groupedUsers || {}).flat();

  return (
    <div>
      <PageHeader title="Task Management" icon="bi-check2-square" subtitle="Assign, track and manage tasks"
        actions={
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchAll} title="Refresh">
              <i className="bi bi-arrow-clockwise"></i>
            </button>
            {canAssign && (
              <button className="btn btn-primary btn-sm"
                onClick={() => setFormModal({ show: true, mode: 'create', data: null })}>
                <i className="bi bi-plus-lg me-1"></i>Assign Task
              </button>
            )}
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="row g-3 mb-3">
        {[
          { title: 'Not Started',  value: sc('not_started'), icon: 'bi-hourglass',              color: '#6c757d' },
          { title: 'In Progress',  value: sc('in_progress'),  icon: 'bi-play-circle',           color: '#0dcaf0' },
          { title: 'Completed',    value: sc('completed'),    icon: 'bi-check-circle-fill',      color: '#198754' },
          { title: 'Delayed',      value: sc('delayed'),      icon: 'bi-exclamation-triangle',   color: '#fd7e14' },
          { title: 'Incomplete',   value: sc('incomplete'),   icon: 'bi-x-circle',               color: '#dc3545' },
        ].map(s => (
          <div className="col-6 col-sm-4 col-lg" key={s.title}>
            <StatCard {...s} />
          </div>
        ))}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {tabItems.map(t => (
          <li className="nav-item" key={t.idx}>
            <button className={`nav-link ${tab === t.idx ? 'active' : ''}`} style={{ fontSize: 13 }}
              onClick={() => setTab(t.idx)}>
              {t.icon && <i className={`bi ${t.icon} me-1`}></i>}{t.label}
            </button>
          </li>
        ))}
      </ul>

      {isReportTab ? <ReportTab /> : isExtensionTab ? <ExtensionRequestsTab canReview={canAssign} onUpdated={fetchAll} /> : (
        <>
          {/* Filters */}
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <div className="input-group" style={{ maxWidth: 260 }}>
              <span className="input-group-text bg-white"><i className="bi bi-search" style={{ fontSize: 12 }}></i></span>
              <input className="form-control form-control-sm" placeholder="Search tasks..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="form-select form-select-sm" style={{ width: 160 }}
              value={filterStatus} onChange={e => { setFilterStatus(e.target.value); }}>
              <option value="">All Statuses</option>
              {Object.entries(STATUS_META)
                .filter(([k]) => !['pending','cancelled'].includes(k))
                .map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 140 }}
              value={filterPriority} onChange={e => { setFilterPriority(e.target.value); }}>
              <option value="">All Priorities</option>
              {Object.entries(PRIORITY_META).map(([v, m]) => (
                <option key={v} value={v}>{m.label}</option>
              ))}
            </select>
            {canAssign && (
              <select className="form-select form-select-sm" style={{ width: 220 }}
                value={filterEmployee} onChange={e => { setFilterEmployee(e.target.value); }}>
                <option value="">All Employees</option>
                {employeeOptions.map((emp) => (
                  <option key={emp._id} value={emp._id}>{emp.name}</option>
                ))}
              </select>
            )}
          </div>

          {loading && <LoadingSpinner />}

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ fontSize: 12, width: '30%' }}>Task</th>
                  <th style={{ fontSize: 12 }}>Assigned To</th>
                  <th style={{ fontSize: 12 }}>Priority</th>
                  <th style={{ fontSize: 12 }}>Status</th>
                  <th style={{ fontSize: 12 }}>Timeliness</th>
                  <th style={{ fontSize: 12 }}>Target Date</th>
                  <th style={{ fontSize: 12 }}>Msgs</th>
                  <th style={{ fontSize: 12 }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tabData.map(task => {
                  const sm = STATUS_META[task.status] || STATUS_META.not_started;
                  const overdue = isOverdue(task);
                  const isMyCreated = (task.assignedBy?._id || task.assignedBy)?.toString() === meId;
                  const isMyTask    = (task.assignedTo?._id || task.assignedTo)?.toString() === meId;
                  const canClaim = task.taskType === 'contribution' && !task.contributeClaimedBy && isFacultyRole;
                  const canDel = isMyCreated || isAdminRole;

                  return (
                    <tr key={task._id} style={overdue ? { background: 'rgba(220,53,69,0.04)' } : {}}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }} className="text-truncate" title={task.title}>
                          {task.title}
                        </div>
                        <div style={{ fontSize: 11, color: '#6c757d' }}>
                          {displayCategory(task, CATEGORY_LABEL[task.category])} · By {task.assignedBy?.firstName} {task.assignedBy?.lastName}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 24, height: 24, fontSize: 10 }}>
                            {task.assignedTo?.firstName?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 500 }}>{task.assignedTo?.firstName} {task.assignedTo?.lastName}</div>
                            <div style={{ fontSize: 11, color: '#6c757d', textTransform: 'capitalize' }}>
                              {(task.assignedTo?.roleKey || task.assignedTo?.role)?.replace(/_/g,' ')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge bg-${PRIORITY_META[task.priority]?.bs || 'primary'}`}
                          style={{ fontSize: 11 }}>{task.priority}</span>
                      </td>
                      <td>
                        <span className={`badge bg-${sm.bs}`} style={{ fontSize: 11 }}>
                          <i className={`bi ${sm.icon} me-1`}></i>{sm.label}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge bg-${task.timelinessStatus === 'overdue' || task.timelinessStatus === 'late' ? 'danger' : task.timelinessStatus === 'early' ? 'success' : 'secondary'}`}
                          style={{ fontSize: 11 }}
                        >
                          {(task.timelinessStatus || 'pending').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 12, color: overdue ? '#dc3545' : '#6c757d', fontWeight: overdue ? 700 : 400 }}>
                          {fmtDate(task.targetDate || task.dueDate)}
                          {overdue && ' ⚠'}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-primary rounded-pill" style={{ fontSize: 10 }}>
                          {task.conversation?.length || 0}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <button className="btn btn-sm btn-outline-secondary" title="View / Conversation"
                            onClick={() => {
                              setDetailTask(task);
                              api.post(`/shared/tasks/${task._id}/acknowledge`).catch(() => {});
                            }} style={{ padding: '2px 6px' }}>
                            <i className="bi bi-eye" style={{ fontSize: 13 }}></i>
                          </button>
                          {(isMyCreated || isAdminRole) && (
                            <button className="btn btn-sm btn-outline-primary" title="Edit"
                              onClick={() => setFormModal({ show: true, mode: 'edit', data: task })}
                              style={{ padding: '2px 6px' }}>
                              <i className="bi bi-pencil" style={{ fontSize: 13 }}></i>
                            </button>
                          )}
                          {canClaim && (
                            <button className="btn btn-sm btn-outline-info" title="Claim"
                              onClick={async () => { await api.post(`/shared/tasks/${task._id}/claim`); fetchAll(); }}
                              style={{ padding: '2px 6px' }}>
                              <i className="bi bi-hand-index-thumb" style={{ fontSize: 13 }}></i>
                            </button>
                          )}
                          {isMyTask && task.status !== 'completed' && (
                            <button className="btn btn-sm btn-outline-success" title="Mark Complete"
                              onClick={() => handleStatusChange(task._id, 'completed')}
                              style={{ padding: '2px 6px' }}>
                              <i className="bi bi-check-circle" style={{ fontSize: 13 }}></i>
                            </button>
                          )}
                          {canDel && (
                            <button className="btn btn-sm btn-outline-danger" title="Delete"
                              onClick={() => setDeleteTask(task)}
                              style={{ padding: '2px 6px' }}>
                              <i className="bi bi-trash" style={{ fontSize: 13 }}></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {tabData.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      <i className="bi bi-check2-square d-block mx-auto mb-2" style={{ fontSize: 36, opacity: 0.3 }}></i>
                      No tasks found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Task Form Modal */}
      <TaskFormModal
        show={formModal.show}
        mode={formModal.mode}
        initialData={formModal.data}
        groupedUsers={groupedUsers}
        onClose={() => setFormModal({ show: false, mode: 'create', data: null })}
        onSaved={handleFormSaved}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        show={Boolean(detailTask)}
        task={detailTask}
        currentUser={user}
        onClose={() => setDetailTask(null)}
        onStatusChange={handleStatusChange}
        onConversationAdded={handleConversationAdded}
      />

      {/* Delete Confirm */}
      {deleteTask && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Delete Task?</h6>
                <button type="button" className="btn-close" onClick={() => setDeleteTask(null)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-0" style={{ fontSize: 13 }}>
                  Are you sure you want to delete <strong>"{deleteTask.title}"</strong>? This cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTask(null)}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
